import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Comment, type User } from "../lib/api";
import Avatar from "./Avatar";
import { LoadingButton } from "./Spinner";
import RelativeTime from "./RelativeTime";
import { useConfirm } from "./ConfirmDialog";
import Markdown from "./Markdown";
import ChipInput from "./ChipInput";
import { memberSlug } from "./MentionField";

interface Props {
  targetKind: "request" | "collection" | "folder" | "project" | "workspace";
  targetId: string;
  me: User;
  workspaceId: string;
}

export default function Comments({ targetKind, targetId, me, workspaceId }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [body, setBody] = useState("");

  const list = useQuery({
    queryKey: ["comments", targetKind, targetId],
    queryFn: () => api.comments.list(targetKind, targetId),
  });
  const membersQ = useQuery({
    queryKey: ["workspace-members", workspaceId],
    queryFn: () => api.workspaces.members(workspaceId),
  });
  const members = membersQ.data?.members ?? [];

  const post = useMutation({
    mutationFn: (text: string) => api.comments.create({ target_kind: targetKind, target_id: targetId, body: text }),
    onSuccess: () => { setBody(""); qc.invalidateQueries({ queryKey: ["comments", targetKind, targetId] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.comments.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["comments", targetKind, targetId] }),
  });

  const comments = list.data?.comments ?? [];

  // Map from slug → member, used in the rendered comments to style mentions.
  const slugToMember = useMemo(() => {
    const m = new Map<string, typeof members[number]>();
    for (const x of members) m.set(memberSlug(x), x);
    return m;
  }, [members]);

  const handleSubmit = () => { if (body.trim()) post.mutate(body.trim()); };

  return (
    <div className="space-y-4 min-w-0">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold mb-1">Comments</h3>
        <p className="text-xs muted mb-3 break-words">Markdown supported. Mention teammates with <code>@</code>.</p>
      </div>

      <ul className="space-y-3 min-w-0">
        {comments.length === 0 && (
          <li className="empty-card">No comments yet — start the thread.</li>
        )}
        {comments.map((c) => (
          <CommentRow
            key={c.id}
            comment={c}
            me={me}
            slugToMember={slugToMember}
            onDelete={async () => {
              if (await confirm({ title: "Delete this comment?", danger: true })) remove.mutate(c.id);
            }}
          />
        ))}
      </ul>

      <form
        onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
        className="rounded-md border border-border bg-surface-2 p-3 space-y-2"
      >
        <ChipInput
          kind="mention"
          value={body}
          onChange={setBody}
          multiline
          rows={3}
          onSubmit={handleSubmit}
          placeholder="Write a comment… (Markdown + @mentions; ⌘↩ to post)"
          suggestions={members.map((m) => ({
            name: memberSlug(m),
            hint: m.name ?? m.email,
            leading: (
              <Avatar
                src={m.picture_url ?? (m.id ? api.avatars.userUrl(m.id) : null)}
                name={m.name}
                email={m.email}
                size={22}
              />
            ),
          }))}
        />
        <div className="flex justify-end">
          <LoadingButton type="submit" className="btn-primary" pending={post.isPending} disabled={!body.trim()}>
            Post
          </LoadingButton>
        </div>
      </form>
    </div>
  );
}

function CommentRow({ comment, me, slugToMember, onDelete }: {
  comment: Comment;
  me: User;
  slugToMember: Map<string, { id: string; email: string; name: string | null; picture_url: string | null }>;
  onDelete: () => void;
}) {
  const isMine = comment.author_id === me.id;
  const src = comment.author_id && comment.author_avatar_updated_at
    ? api.avatars.userUrl(comment.author_id, comment.author_avatar_updated_at)
    : null;
  // Convert `@slug` into a markdown link with a custom scheme; we override
  // the link renderer below to draw the mention chip. Only known members
  // get the chip — unknown @-strings remain plain text.
  const decorated = useMemo(
    () => decorateMentions(comment.body, slugToMember),
    [comment.body, slugToMember],
  );
  return (
    <li className="flex gap-3">
      <Avatar src={src} name={comment.author_name} email={comment.author_email ?? "unknown@unknown"} size={32} />
      <div className="flex-1 min-w-0 rounded-md border border-border bg-surface-2 p-3">
        <div className="flex items-center gap-2 text-xs muted-strong mb-1.5 flex-wrap min-w-0">
          <span className="font-semibold text-text truncate min-w-0">{comment.author_name ?? comment.author_email ?? "unknown"}</span>
          <span>·</span>
          <RelativeTime iso={comment.created_at} />
          {comment.edited_at && <span className="italic">(edited)</span>}
          {isMine && (
            <button className="ml-auto text-danger hover:underline" onClick={onDelete}>Delete</button>
          )}
        </div>
        <div className="text-sm min-w-0 break-words"><Markdown source={decorated} /></div>
      </div>
    </li>
  );
}

// Walk the raw source and replace `@slug` tokens that match a workspace
// member with a markdown link to `mention://slug`. Markdown.tsx renders
// these as styled chips.
function decorateMentions(
  source: string,
  slugs: Map<string, { name: string | null; email: string }>,
): string {
  return source.replace(/(^|\W)@([A-Za-z0-9_\-\.]+)/g, (full, lead, slug) => {
    if (!slugs.has(slug.toLowerCase())) return full;
    const member = slugs.get(slug.toLowerCase())!;
    const label = member.name ?? member.email.split("@")[0];
    return `${lead}[@${label}](mention://${slug.toLowerCase()})`;
  });
}

